import { maintenanceSortables, VIRTUAL_MAINTENANCE_PREFIX } from "@waku-care/types/contracts/internal";

import { type PaginatedTransformedMaintenance } from "@/api/resources/maintenances/types";
import { defaultMaintenanceItemsStatusProgressDetails } from "@/api/resources/maintenancesToDevices/utils";
import { type PaginatedMaintenanceStrategy } from "@/api/resources/maintenanceStrategies/types";
import { MaintenanceStatus, type Maintenance } from "@/supabase.types";
import {
  getDateDurationByInterval,
  getDateEndOfDay,
  getDateIsBefore,
  getDateNow,
  getDatePlusMinus,
  getDurationStringFromDates,
} from "@/utils/dates";

export { VIRTUAL_MAINTENANCE_PREFIX };

// table columns the backend list can be sorted by (sort_by `maintenances.<column>`, see maintenanceSortables)
export const MAINTENANCE_SORTABLE_COLUMNS: string[] = maintenanceSortables.map((sortable) => sortable.column);

// fields searched in locally filtered maintenance lists (eg. virtual maintenances, chart drill-downs) — the backend
// list searches maintenanceSearchables
export const MAINTENANCE_LOCAL_SEARCHABLES = [
  "title",
  "notes",
  "skip_reason",
  "public_id",
  "maintenance_tags.name",
  "maintenance_strategy.title",
];

export const maintenanceStatuses = [
  MaintenanceStatus.scheduled,
  MaintenanceStatus.started,
  MaintenanceStatus.finished,
  MaintenanceStatus.skipped,
];
export const editableMaintenanceStatuses = [MaintenanceStatus.scheduled, MaintenanceStatus.started];
export const isMaintenanceStatus = (status: string): status is MaintenanceStatus =>
  maintenanceStatuses.includes(status as MaintenanceStatus);

export const isVirtualMaintenance = (maintenance?: Pick<Maintenance, "id">) =>
  Boolean(maintenance?.id) && maintenance?.id.startsWith(VIRTUAL_MAINTENANCE_PREFIX);

export const getMaintenanceDatesByStatus = (
  maintenance: Pick<
    Maintenance,
    "status" | "planned_date" | "planned_date_until" | "scheduled_at" | "planned_at" | "started_at" | "resolved_at"
  >,
) => {
  let startDate = maintenance.planned_date ?? maintenance.planned_at ?? maintenance.scheduled_at ?? "";
  if (maintenance.status === MaintenanceStatus.started) startDate = maintenance.started_at ?? "";
  if (maintenance.status === MaintenanceStatus.finished) startDate = maintenance.started_at ?? "";
  if (maintenance.status === MaintenanceStatus.skipped) startDate = maintenance.resolved_at ?? "";

  let endDate = maintenance.planned_date_until ?? getDateEndOfDay(maintenance.planned_date ?? "");
  if (maintenance.status === MaintenanceStatus.started) endDate = getDateEndOfDay(maintenance.started_at ?? "");
  if (maintenance.status === MaintenanceStatus.skipped)
    endDate = getDatePlusMinus(maintenance.resolved_at ?? "", 1, "milliseconds");
  if (maintenance.status === MaintenanceStatus.finished) endDate = maintenance.resolved_at ?? "";

  if (getDateIsBefore(endDate, startDate)) {
    endDate = getDateEndOfDay(startDate);
  }

  return {
    startDate,
    endDate,
  };
};

interface GetMaintenanceVirtualProps {
  maintenanceStrategy: PaginatedMaintenanceStrategy;
  date: string;
  dateUntil?: string;
  durationInterval?: string;
}
export const getMaintenanceVirtual = ({
  maintenanceStrategy,
  date,
  dateUntil,
  durationInterval,
}: GetMaintenanceVirtualProps): PaginatedTransformedMaintenance => {
  const {
    id: maintenance_strategy_id,
    user_id,
    assignee_id,
    title,
    workspace_id,
    deployment_id,
    maintenance_strategies_to_devices,
  } = maintenanceStrategy;

  // if we have a duration, we can derive the until date
  let derivedUntil =
    dateUntil ??
    (date && durationInterval
      ? getDatePlusMinus(date, getDateDurationByInterval(durationInterval), "milliseconds")
      : null);
  // if we have a until date, we can derive the duration
  let derivedDuration = durationInterval ?? (date && dateUntil ? getDurationStringFromDates(date, dateUntil) : null);

  return {
    id: `${VIRTUAL_MAINTENANCE_PREFIX}:${maintenance_strategy_id}@${date}`,
    maintenance_strategy_id,
    serial_id: 1,
    serial_id_prefix: "---",
    public_id: "---",
    created_at: getDateNow(),
    updated_at: getDateNow(),
    scheduled_at: date,
    scheduled_until: derivedUntil, // is derived in DB
    scheduled_duration: derivedDuration,
    planned_at: null,
    planned_until: null, // is derived in DB
    planned_duration: null,
    planned_date: date,
    planned_date_until: derivedUntil,
    notes: "",
    maintenance_items: [],
    cases_to_maintenances: [],
    child_maintenances: [],
    parent_maintenances: [],
    resolved_at: null,
    skip_reason: null,
    started_at: null,
    status: MaintenanceStatus.scheduled,
    title,
    user_id,
    assignee_id,
    assignee: null,
    workspace_id,
    deployment_id,
    resolution_duration: null,
    content_resolution_duration: null,
    content_planned_duration: null,
    maintenance_strategy: maintenanceStrategy,
    service_jobs: [],
    maintenances_to_devices: [],
    maintenance_tags: [],
    part_location_id: null,
    status_progress: 0,
    status_progress_max: 0,
    status_progress_details: defaultMaintenanceItemsStatusProgressDetails, // TODO: actually not correct, we would need to count overall checklist_template_items length
    planned_devices_count: maintenance_strategies_to_devices.length,
  };
};
